import { CommonModule } from '@angular/common';
import { Component, HostListener, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { AppState } from '../common';
import { DocumentLinkUploadContextService } from './document-link-upload-context.service';

@Component({
  selector: 'app-global-document-drop',
  standalone: true,
  imports: [CommonModule, MatIconModule, TranslateModule],
  template: `
    @if (isVisible()) {
      <div class="drop-overlay">
        <div class="drop-content">
          <mat-icon>upload</mat-icon>
          <p class="title">{{ 'Upload_Documents_tr' | translate }}</p>
          <p class="hint">{{ 'Upload_relevant_Documents_tr' | translate }}</p>
        </div>
      </div>
    }
  `,
  styles: [
    `
      :host {
        pointer-events: none;
        position: fixed;
        inset: 0;
        z-index: 2000;
      }
      .drop-overlay {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        background: rgba(33, 33, 33, 0.6);
        color: #fff;
        backdrop-filter: blur(2px);
      }
      .drop-content {
        pointer-events: none;
        text-align: center;
        border: 2px dashed rgba(255, 255, 255, 0.7);
        border-radius: 16px;
        padding: 32px 48px;
        max-width: 420px;
        background: rgba(0, 0, 0, 0.35);
      }
      mat-icon {
        font-size: 48px;
        height: 48px;
        width: 48px;
        margin-bottom: 12px;
      }
      .title {
        font-size: 20px;
        font-weight: 600;
        margin: 0 0 8px;
      }
      .hint {
        margin: 0;
        font-size: 16px;
        opacity: 0.85;
      }
    `,
  ],
})
export class GlobalDocumentDropComponent {
  private readonly toastr = inject(ToastrService);
  private readonly router = inject(Router);
  private readonly appState = inject(AppState);
  private readonly linkUploadContext = inject(DocumentLinkUploadContextService);
  private dragDepth = 0;
  private readonly dragging = signal(false);

  protected readonly isVisible = computed(() => this.dragging() && !this.getBlockingMessage());

  @HostListener('window:dragenter', ['$event'])
  handleDragEnter(event: DragEvent) {
    if (!this.hasFiles(event)) {
      return;
    }
    if (this.getBlockingMessage()) {
      event.preventDefault();
      this.dragging.set(false);
      return;
    }
    event.preventDefault();
    this.dragDepth++;
    this.dragging.set(true);
  }

  @HostListener('window:dragover', ['$event'])
  handleDragOver(event: DragEvent) {
    if (this.dragging()) {
      event.preventDefault();
    }
  }

  @HostListener('window:dragleave', ['$event'])
  handleDragLeave(event: DragEvent) {
    if (!this.dragging()) {
      return;
    }
    event.preventDefault();
    if (!event.relatedTarget) {
      this.resetDragState();
      return;
    }
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) {
      this.dragging.set(false);
    }
  }

  @HostListener('window:drop', ['$event'])
  async handleDrop(event: DragEvent) {
    if (event.defaultPrevented) {
      this.resetDragState();
      return;
    }
    if (!this.hasFiles(event)) {
      return;
    }
    event.preventDefault();
    const blockMessage = this.getBlockingMessage();
    this.resetDragState();

    if (blockMessage) {
      this.toastr.warning(blockMessage);
      return;
    }

    const files = Array.from(event.dataTransfer?.files ?? []).filter((file) => file.type || file.size);
    if (!files.length) {
      return;
    }
    try {
      if (this.linkUploadContext.hasUploadHandler()) {
        await this.linkUploadContext.uploadFiles(files);
        return;
      }
      this.linkUploadContext.setPendingFiles(files);
      await this.openDocumentManagerWithFiles();
    } catch (error) {
      console.error('Global document drop failed', error);
    }
  }

  @HostListener('window:mouseleave', ['$event'])
  handleWindowMouseLeave(event: MouseEvent) {
    if (!this.dragging()) {
      return;
    }
    if (!event.relatedTarget) {
      this.resetDragState();
    }
  }

  @HostListener('window:blur')
  handleWindowBlur() {
    if (this.dragging()) {
      this.resetDragState();
    }
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyDown(event: KeyboardEvent) {
    if (event.key === 'Escape' && this.dragging()) {
      this.resetDragState();
    }
  }

  private resetDragState() {
    this.dragDepth = 0;
    this.dragging.set(false);
  }

  private hasFiles(event: DragEvent) {
    const types = event.dataTransfer?.types;
    if (!types) {
      return false;
    }
    return Array.from(types).includes('Files');
  }

  private getBlockingMessage(): string | null {
    if (document.querySelector('app-tour-popover') || document.querySelector('app-tour-confirm-dialog')) {
      return 'Unable to upload documents please close other dialogs first';
    }
    return null;
  }

  private async openDocumentManagerWithFiles() {
    const cs = this.appState.companySite$.value;
    const companyId = cs?.company?.id;
    const siteId = cs?.site?.id ?? cs?.company?.sites?.[0]?.id;
    if (!companyId || !siteId) {
      return;
    }
    await this.router.navigate(['/company', companyId, 'site', siteId, 'document-directory']);
  }
}
